import Link from "next/link"
import Image from "next/image"
import { Mail, Phone, MapPin } from "lucide-react"

export default function Footer() {
  return (
    <footer className="bg-[#444446] text-white">
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16">
          {/* Logo and About */}
          <div>
            <Link href="/" className="inline-block mb-4">
              <Image src="/logo.svg" alt="BoardChampions" width={180} height={40} className="h-10 w-auto" />
            </Link>
            <p className="text-sm text-gray-300 mb-8 leading-relaxed font-light">
              BoardChampions connects businesses with expert talent for board, fractional, and project-based roles.
            </p>
            <div className="flex space-x-3">
              <Link
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Connect with us on LinkedIn"
                className="w-10 h-10 bg-[#738fc1] rounded-full flex items-center justify-center hover:bg-[#5d7cb8] transition-all duration-300"
              >
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </Link>
              <Link
                href="https://x.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on X (formerly Twitter)"
                className="w-10 h-10 bg-[#738fc1] rounded-full flex items-center justify-center hover:bg-[#5d7cb8] transition-all duration-300"
              >
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </Link>
              <Link
                href="https://youtube.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Subscribe to our YouTube channel"
                className="w-10 h-10 bg-[#738fc1] rounded-full flex items-center justify-center hover:bg-[#5d7cb8] transition-all duration-300"
              >
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </Link>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-akrive-grotesk font-bold text-xl mb-8 text-[#9eb4d8] uppercase tracking-wider">Quick Links</h3>
            <ul className="space-y-4">
              <li>
                <Link href="/search" className="text-gray-300 hover:text-white transition-all duration-300 hover:translate-x-2 inline-block text-sm font-light">
                  Search Talent
                </Link>
              </li>
              <li>
                <Link href="/companies" className="text-gray-300 hover:text-white transition-all duration-300 hover:translate-x-2 inline-block text-sm font-light">
                  For Companies
                </Link>
              </li>
              <li>
                <Link href="/process/our-process" className="text-gray-300 hover:text-white transition-all duration-300 hover:translate-x-2 inline-block text-sm font-light">
                  Our Process
                </Link>
              </li>
              <li>
                <Link href="/about" className="text-gray-300 hover:text-white transition-all duration-300 hover:translate-x-2 inline-block text-sm font-light">
                  About Us
                </Link>
              </li>
              <li>
                <Link href="/contact-us" className="text-gray-300 hover:text-white transition-all duration-300 hover:translate-x-2 inline-block text-sm font-light">
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="font-akrive-grotesk font-bold text-xl mb-8 text-[#9eb4d8] uppercase tracking-wider">Resources</h3>
            <ul className="space-y-4">
              <li>
                <Link href="/signup" className="text-gray-300 hover:text-white transition-all duration-300 hover:translate-x-2 inline-block text-sm font-light">
                  Join Our Network
                </Link>
              </li>
              <li>
                <Link href="/browse" className="text-gray-300 hover:text-white transition-all duration-300 hover:translate-x-2 inline-block text-sm font-light">
                  Browse Profiles
                </Link>
              </li>
              <li>
                <Link href="/saved-profiles" className="text-gray-300 hover:text-white transition-all duration-300 hover:translate-x-2 inline-block text-sm font-light">
                  Saved Profiles
                </Link>
              </li>
              <li>
                <Link href="/companies#services" className="text-gray-300 hover:text-white transition-all duration-300 hover:translate-x-2 inline-block text-sm font-light">
                  Our Services
                </Link>
              </li>
              <li>
                <Link href="/privacy-policy" className="text-gray-300 hover:text-white transition-all duration-300 hover:translate-x-2 inline-block text-sm font-light">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link href="https://londonkeynotespeakersagency.com/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-all duration-300 hover:translate-x-2 inline-block text-sm font-light">
                  London Keynote Speakers
                </Link>
              </li>
              <li>
                <Link href="https://speakout.uk/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-all duration-300 hover:translate-x-2 inline-block text-sm font-light">
                  SpeakOut Speakers Agency
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-akrive-grotesk font-bold text-xl mb-8 text-[#9eb4d8] uppercase tracking-wider">Contact Us</h3>
            <ul className="space-y-5">
              <li className="flex items-start">
                <MapPin className="h-5 w-5 mr-3 text-[#7294c7] flex-shrink-0 mt-0.5" />
                <span className="text-gray-300 text-sm font-light leading-relaxed">1st Floor, 17 Hanover Square, Mayfair, London, W1S 1BN</span>
              </li>
              <li className="flex items-center">
                <Phone className="h-5 w-5 mr-3 text-[#7294c7] flex-shrink-0" />
                <Link href="tel:08453313031" className="text-gray-300 hover:text-white transition-colors font-light text-sm">
                  0845 331 3031
                </Link>
              </li>
              <li className="flex items-center">
                <Mail className="h-5 w-5 mr-3 text-[#7294c7] flex-shrink-0" />
                <Link
                  href="mailto:info@boardchampions.co.uk"
                  className="text-gray-300 hover:text-white transition-colors font-light text-sm"
                >
                  info@boardchampions.co.uk
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-600/30 mt-16 pt-10 text-sm text-gray-400">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p>&copy; {new Date().getFullYear()} BoardChampions. All rights reserved.</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <Link href="/privacy-policy" className="hover:text-white transition-all duration-300 border-b border-transparent hover:border-white pb-1">
                Privacy Policy
              </Link>
              <Link href="/privacy-policy#terms" className="hover:text-white transition-all duration-300 border-b border-transparent hover:border-white pb-1">
                Terms of Service
              </Link>
              <Link href="/privacy-policy#cookies" className="hover:text-white transition-all duration-300 border-b border-transparent hover:border-white pb-1">
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
